import { Action } from 'redux';
import { put, select } from '@redux-saga/core/effects';
import { BasketActions, BasketActionTypes, BasketLoadingItem, LoadingItemTypes } from '../basket.types';
import { ACTION_CREATOR_NAME, userHelper, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { AppState, ErrorAction } from '../../root.types';
import { api } from 'repositories/api';
import { BasketItemModel, BasketDetailsModel } from 'models/Basket';
import { initBasketPageSaga } from './initBasketPage';
import { callBasketSaga, BasketSagaReturnType } from 'store/sagaHelpers';

export interface RemoveSofaglideAction extends Action<BasketActionTypes.REMOVE_SOFAGLIDE> {
    itemProductCode: string;
}

export interface RemoveSofaglideActionStart extends Action<BasketActionTypes.REMOVE_SOFAGLIDE_START> {
    loadingItem: BasketLoadingItem;
}

export interface RemoveSofaglideActionSuccess extends Action<BasketActionTypes.REMOVE_SOFAGLIDE_SUCCESS> {
    loadingItem: BasketLoadingItem;
}

export interface RemoveSofaglideActionError extends ErrorAction<BasketActionTypes.REMOVE_SOFAGLIDE_ERROR> {
    loadingItem: BasketLoadingItem;
}

export const removeSofaglide: (itemProductCode: string) => RemoveSofaglideAction =
    (itemProductCode: string) => ({
        type: BasketActionTypes.REMOVE_SOFAGLIDE,
        itemProductCode
    });
removeSofaglide[ACTION_CREATOR_NAME] = 'removeSofaglide';

export type RemoveSofaglideFunc = (...args: Parameters<typeof removeSofaglide>) => void;

export function* removeSofaglideSaga({ itemProductCode }: RemoveSofaglideAction) {
    yield put<BasketActions>({
        type: BasketActionTypes.REMOVE_SOFAGLIDE_START,
        loadingItem: { id: itemProductCode, loadingType: LoadingItemTypes.RemoveSofaglide }
    });
}

export function* removeSofaglideQueueSaga({ loadingItem }: RemoveSofaglideActionStart): BasketSagaReturnType {
    try {
        let state: AppState = yield select();
        const basket = state.basketState.basket!;
        const basketId = state.basketState.basket!.guid;

        const sofaglideEntry = basket.entries?.find((entry: BasketItemModel) => (
            entry?.linkedSofaglide?.baseProductCode === loadingItem.id
        ));

        if (sofaglideEntry) {
            yield api.hybris.removeBasketItem(
                userHelper.getCurrentUserName(),
                basketId,
                sofaglideEntry.linkedSofaglide!.entryNumber,
                loadingItem.id
            );

            const sofaglide = basket.entries.find((entry: BasketItemModel) => entry.entryNumber === sofaglideEntry.linkedSofaglide!.entryNumber)!;

            yield callBasketSaga(initBasketPageSaga, {
                type: BasketActionTypes.INIT_BASKET_PAGE
            });

            state = yield select();
            sendAnalytics(sofaglide, sofaglideEntry.linkedSofaglide!.quantity, state.basketState.basket!);
        }

        yield put<BasketActions>({
            type: BasketActionTypes.REMOVE_SOFAGLIDE_SUCCESS,
            loadingItem
        });

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.REMOVE_SOFAGLIDE_ERROR,
            loadingItem,
            error: errorToBasketErrorModel(error, 'removeSofaglideQueueSaga')
        });

        return false;
    }
}

function sendAnalytics(item: BasketItemModel, removedQuantity: number, updatedBasket: BasketDetailsModel): void {
    analyticsHelper.withLogging(() => {
        api.google.removeSofaglideProduct(item.product.code, removedQuantity, item.product.categories[0]?.code, item.basePrice);
        api.exponea.removeFromCart(updatedBasket, item, removedQuantity);
    }, 'removeSofaglideQueueSaga');
}
